import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Battery as BatteryIcon, Plug } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import { fmtInt, relativeTime } from "../lib/format";
import { deviceLastSeen, deviceReadingsCount } from "../lib/deviceMetadata";
import {
  deriveDeviceStatus, deviceSource, isBatteryGroupDevice, providerLabel,
} from "../lib/deviceStatus";
import HomeWizardBatteryGroupDetail from "../components/devices/HomeWizardBatteryGroupDetail";

const BATTERY_POLL_MS = 30_000;

/**
 * Dedicated Battery page — Sprint 7.3.
 *
 * Replaces the static "Planned for the next release" placeholder with
 * live HomeWizard Battery Group telemetry. Uses the exact same
 * detection logic (`isBatteryGroupDevice`) and rendering component
 * (`HomeWizardBatteryGroupDetail`) the Devices detail panel uses, so
 * the two surfaces never drift.
 *
 * Per-sprint scope: no SOC / cycles / per-battery cards / autonomy
 * contribution. Those land later when the underlying metrics start
 * flowing.
 *
 * Empty state: "No HomeWizard Battery Group detected yet." when no
 * matching device exists on the site.
 */
function StatusPill({ status, testid }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
      style={{ color: status.accent, backgroundColor: `${status.accent}15`, border: `1px solid ${status.accent}30` }}
      data-testid={testid}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status.accent }} />
      {status.label}
    </span>
  );
}

function ReadingsTimeline({ readings }) {
  if (!readings.length) {
    return (
      <div className="text-xs text-muted-foreground py-6 text-center" data-testid="battery-readings-empty">
        No readings yet for this battery group.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="battery-readings-table">
        <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
          <tr>
            <th className="text-left px-5 py-2.5">Timestamp</th>
            <th className="text-left px-3 py-2.5">Metric</th>
            <th className="text-right px-3 py-2.5">Value</th>
            <th className="text-right px-5 py-2.5">Unit</th>
          </tr>
        </thead>
        <tbody className="font-mono text-[12px]">
          {readings.slice(0, 20).map((r) => (
            <tr key={r.id} className="border-b border-border/60 hover:bg-secondary/30">
              <td className="px-5 py-2 font-sans">
                {new Date(r.timestamp).toLocaleString([], {
                  month: "short", day: "2-digit",
                  hour: "2-digit", minute: "2-digit", second: "2-digit",
                })}
              </td>
              <td className="px-3">{r.metric}</td>
              <td className="text-right px-3 tabular-nums">
                {typeof r.value === "number" ? r.value.toFixed(3).replace(/\.?0+$/, "") : "—"}
              </td>
              <td className="text-right px-5 text-muted-foreground">{r.unit || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PageShell({ children }) {
  return (
    <div className="space-y-6 pb-12" data-testid="battery-page">
      <header>
        <div className="label-tiny mb-1.5 flex items-center gap-2">
          <BatteryIcon className="w-3.5 h-3.5" /> Battery
        </div>
        <h1 className="font-heading text-3xl font-semibold">Home battery</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Live HomeWizard Battery Group telemetry — group power, target,
          mode, capacity, and charge / discharge permissions.
        </p>
      </header>
      {children}
    </div>
  );
}

function BatteryEmptyState() {
  return (
    <section
      className="card-base p-10 text-center max-w-xl"
      data-testid="battery-empty-state"
    >
      <div className="w-12 h-12 mx-auto rounded-xl bg-secondary grid place-items-center">
        <BatteryIcon className="w-6 h-6 text-muted-foreground" />
      </div>
      <div className="font-heading text-lg font-semibold mt-4">
        No HomeWizard Battery Group detected yet.
      </div>
      <p className="text-sm text-muted-foreground mt-2">
        Once your local bridge starts ingesting battery readings, they will appear here.
        See <span className="font-mono">/bridge/dh_bridge.py</span> for the
        battery group polling loop.
      </p>
    </section>
  );
}

export default function Battery() {
  const { currentSite } = useSite();
  const [device, setDevice] = useState(null);
  const [readings, setReadings] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    if (!currentSite) return;
    try {
      const [devRes, runsRes] = await Promise.all([
        api.get(`/sites/${currentSite.id}/devices`),
        api.get(`/sites/${currentSite.id}/sync-runs?limit=25`),
      ]);
      const batt = (devRes.data || []).find(isBatteryGroupDevice) || null;
      setDevice(batt);
      setSyncRuns(runsRes.data || []);
      if (batt) {
        const { data } = await api.get(
          `/sites/${currentSite.id}/devices/${batt.id}/readings?limit=100`,
        );
        setReadings(data || []);
      } else {
        setReadings([]);
      }
      setErr(null);
    } catch (e) {
      setErr(e.userMessage || "Could not load battery data");
    } finally {
      setLoading(false);
    }
  }, [currentSite]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, BATTERY_POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  const status = useMemo(
    () => (device ? deriveDeviceStatus(deviceLastSeen(device)) : null),
    [device],
  );

  const latestBatteryRun = useMemo(() => {
    if (!device) return null;
    return syncRuns.find((r) => (r.summary_json || {}).device_id === device.id) || null;
  }, [syncRuns, device]);

  if (loading) {
    return (
      <PageShell>
        <div className="text-sm text-muted-foreground" data-testid="battery-loading">
          Loading battery…
        </div>
      </PageShell>
    );
  }

  if (err && !device) {
    return (
      <PageShell>
        <div className="text-xs text-[#EF4444]" data-testid="battery-error">{err}</div>
        <BatteryEmptyState />
      </PageShell>
    );
  }

  if (!device) {
    return (
      <PageShell>
        <BatteryEmptyState />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="card-base p-5" data-testid="battery-detail">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
              {providerLabel(device.provider)} · {deviceSource(device.provider)}
            </div>
            <h2 className="font-heading text-xl font-semibold mt-1">
              {device.name || "HomeWizard Battery Group"}
            </h2>
            <div className="text-[11px] text-muted-foreground mt-1">
              external id <span className="font-mono">{device.external_id}</span>
              {" · "}domain {device.domain || "battery"}
            </div>
          </div>
          {status && <StatusPill status={status} testid="battery-status" />}
        </div>

        <div className="mt-5">
          <HomeWizardBatteryGroupDetail
            readings={readings}
            testid="battery-page-group-detail"
          />
        </div>

        <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          <span data-testid="battery-last-seen">
            Last seen: <span className="text-foreground">{relativeTime(deviceLastSeen(device))}</span>
          </span>
          <span data-testid="battery-readings-count">
            Readings stored: <span className="text-foreground tabular-nums">{fmtInt(deviceReadingsCount(device))}</span>
          </span>
          {latestBatteryRun && (
            <span data-testid="battery-last-sync">
              Last bridge sync:{" "}
              <span className="text-foreground">
                {relativeTime(latestBatteryRun.completed_at || latestBatteryRun.started_at)}
              </span>{" "}· status{" "}
              <span className="text-foreground">{latestBatteryRun.status}</span>
            </span>
          )}
        </div>
      </section>

      <section className="card-base overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="label-tiny flex items-center gap-2">
            <Plug className="w-3 h-3" /> Recent readings · last {Math.min(20, readings.length)}
          </div>
          <div className="text-[11px] text-muted-foreground">auto-refresh every 30 s</div>
        </div>
        <ReadingsTimeline readings={readings} />
      </section>
    </PageShell>
  );
}
